// services/vehicleService.ts
import { firestoreService } from '@/utils/firestoreService'
import { type Vehicle } from '@/types/Vehicle'
import { useSettingsStore } from '@/stores/settings'

const COLLECTION = 'vehicles'

export const vehicleService = {
  get: (id: string) => firestoreService.get<Vehicle>(COLLECTION, id),

  list: () => firestoreService.list<Vehicle>(COLLECTION),

  queryByTag: (tagNumber: string) =>
    firestoreService.query<Vehicle>(COLLECTION, [
      { field: 'tagNumber', op: '==', value: tagNumber.toUpperCase() },
    ]),

  add: async (vehicle: Vehicle): Promise<Vehicle> => {
    vehicle.tagNumber = vehicle.tagNumber.trim().toUpperCase()
    const id = await firestoreService.add<Vehicle>(COLLECTION, vehicle)
    const saved = await firestoreService.get<Vehicle>(COLLECTION, id)
    return { ...(vehicle as Vehicle), id, ...(saved || {}) }
  },

  update: async (vehicle: Vehicle): Promise<Vehicle> => {
    if (!vehicle.id) throw new Error('Vehicle ID is required for update')
    vehicle.tagNumber = vehicle.tagNumber.trim().toUpperCase()
    await firestoreService.set<Vehicle>(COLLECTION, { ...vehicle, id: vehicle.id })
    const saved = await firestoreService.get<Vehicle>(COLLECTION, vehicle.id)
    return saved as Vehicle
  },

  delete: (id: string) => firestoreService.delete(COLLECTION, id),

  /**
   * Search for vehicles by tag number
   */
  searchByTag: async (tagNumber: string): Promise<Vehicle[]> => {
    if (!tagNumber.trim()) return []

    try {
      return await vehicleService.queryByTag(tagNumber.trim())
    } catch (error) {
      console.error('Failed to search vehicles by tag:', error)
      throw new Error('Failed to search vehicles')
    }
  },

  /**
   * Validate vehicle data for transaction requirements using configurable rules
   */
  validateVehicleForTransaction(vehicle: Partial<Vehicle>): {
    isValid: boolean
    errors: string[]
  } {
    const errors: string[] = []
    const settingsStore = useSettingsStore()
    const fieldConfig = settingsStore.transactionSettings.vehicleFieldConfig
    const validationRules = settingsStore.transactionSettings.vehicleValidationRules

    // Get enabled fields from configuration
    const enabledFields = fieldConfig.filter((f) => f.enabled)

    // Apply custom validation rules
    validationRules
      .filter((rule) => rule.enabled)
      .forEach((rule) => {
        const ruleErrors = this.evaluateRule(rule, vehicle, enabledFields)
        errors.push(...ruleErrors)
      })

    return {
      isValid: errors.length === 0,
      errors,
    }
  },

  /**
   * Evaluate a single validation rule
   */
  evaluateRule(
    rule: { id: string; type: string; operator?: string; fields: string[]; message: string },
    vehicle: Partial<Vehicle>,
    enabledFields: { key: string; label: string; enabled: boolean }[],
  ): string[] {
    switch (rule.type) {
      case 'business':
        return this.evaluateBusinessRule(rule, vehicle, enabledFields)
      case 'group':
        return this.evaluateGroupRule(rule, vehicle, enabledFields)
      case 'conditional':
        return this.evaluateConditionalRule(rule, vehicle, enabledFields)
      case 'required':
        return this.evaluateRequiredRule(rule, vehicle, enabledFields)
      default:
        return []
    }
  },

  /**
   * Evaluate business rules (complex logic like VIN OR (tag + state))
   */
  evaluateBusinessRule(
    rule: { id: string; type: string; fields: string[] },
    vehicle: Partial<Vehicle>,
    enabledFields: { key: string; label: string; enabled: boolean }[],
  ): string[] {
    const errors: string[] = []

    if (rule.id === 'vin-or-identification') {
      const hasVin = !!vehicle.vin && vehicle.vin.trim()
      const hasTag = !!vehicle.tagNumber && vehicle.tagNumber.trim()
      const hasState = !!vehicle.state && vehicle.state.trim()

      // Check if VIN field is enabled
      const vinField = enabledFields.find((f) => f.key === 'vin')
      const tagField = enabledFields.find((f) => f.key === 'tagNumber')
      const stateField = enabledFields.find((f) => f.key === 'state')

      if (!hasVin) {
        // If VIN is not provided, check if tag and state are both available and enabled
        if (!vinField || !vinField.enabled) {
          // VIN field is disabled, so we need tag + state
          if (tagField && tagField.enabled && !hasTag) {
            errors.push('Tag number is required when VIN is not available')
          }
          if (stateField && stateField.enabled && !hasState) {
            errors.push('State is required when VIN is not available')
          }
        } else {
          // VIN field is enabled but not provided, need tag + state
          if (tagField && tagField.enabled && !hasTag) {
            errors.push('Tag number is required when VIN is missing')
          }
          if (stateField && stateField.enabled && !hasState) {
            errors.push('State is required when VIN is missing')
          }
        }
      }
    }

    return errors
  },

  /**
   * Evaluate group rules (AND/OR logic for multiple fields)
   */
  evaluateGroupRule(
    rule: { id: string; type: string; operator?: string; fields: string[] },
    vehicle: Partial<Vehicle>,
    enabledFields: { key: string; label: string; enabled: boolean }[],
  ): string[] {
    const errors: string[] = []

    if (rule.operator === 'AND') {
      // All fields must be present
      const missingFields = rule.fields.filter((fieldKey: string) => {
        const field = enabledFields.find((f) => f.key === fieldKey)
        if (!field || !field.enabled) return false

        const value = vehicle[fieldKey as keyof Vehicle]
        return !value || (typeof value === 'string' && !value.trim())
      })

      if (missingFields.length > 0) {
        const fieldLabels = missingFields.map((key: string) => {
          const field = enabledFields.find((f) => f.key === key)
          return field ? field.label : key
        })
        errors.push(`${fieldLabels.join(', ')} are required`)
      }
    } else if (rule.operator === 'OR') {
      // At least one field must be present
      const hasAnyField = rule.fields.some((fieldKey: string) => {
        const field = enabledFields.find((f) => f.key === fieldKey)
        if (!field || !field.enabled) return false

        const value = vehicle[fieldKey as keyof Vehicle]
        return value && (typeof value !== 'string' || value.trim())
      })

      if (!hasAnyField) {
        const fieldLabels = rule.fields
          .map((key: string) => {
            const field = enabledFields.find((f) => f.key === key)
            return field ? field.label : key
          })
          .filter(Boolean)
        errors.push(`At least one of ${fieldLabels.join(', ')} is required`)
      }
    }

    return errors
  },

  /**
   * Evaluate conditional rules (IF-THEN logic)
   */
  evaluateConditionalRule(
    _rule: { id: string; type: string; fields: string[] },
    _vehicle: Partial<Vehicle>,
    _enabledFields: { key: string; label: string; enabled: boolean }[],
  ): string[] {
    // TODO: Implement conditional logic
    return []
  },

  /**
   * Evaluate required field rules
   */
  evaluateRequiredRule(
    rule: { id: string; type: string; fields: string[] },
    vehicle: Partial<Vehicle>,
    enabledFields: { key: string; label: string; enabled: boolean }[],
  ): string[] {
    const errors: string[] = []

    rule.fields.forEach((fieldKey: string) => {
      const field = enabledFields.find((f) => f.key === fieldKey)
      if (!field || !field.enabled) return

      const value = vehicle[fieldKey as keyof Vehicle]
      if (!value || (typeof value === 'string' && !value.trim())) {
        errors.push(`${field.label} is required`)
      }
    })

    return errors
  },

  /**
   * Create a new vehicle with validation
   */
  createVehicle: async (
    data: Partial<Vehicle>,
  ): Promise<{ success: true; vehicle: Vehicle } | { success: false; errors: string[] }> => {
    const validation = vehicleService.validateVehicleForTransaction(data)
    if (!validation.isValid) {
      return { success: false, errors: validation.errors }
    }

    const vehicle: Vehicle = {
      tagNumber: data.tagNumber || '',
      state: data.state || '',
      year: data.year,
      make: data.make || '',
      model: data.model || '',
      color: data.color || '',
      vin: data.vin || '',
      notes: data.notes || '',
      createdAt: new Date(),
      updatedAt: new Date(),
      ...data,
    }

    const createdVehicle = await vehicleService.add(vehicle)
    return { success: true, vehicle: createdVehicle }
  },

  /**
   * Update a vehicle with validation
   */
  updateVehicle: async (
    id: string,
    data: Partial<Vehicle>,
  ): Promise<{ success: true; vehicle: Vehicle } | { success: false; errors: string[] }> => {
    const existingVehicle = await vehicleService.get(id)
    if (!existingVehicle) {
      return { success: false, errors: ['Vehicle not found'] }
    }

    const updatedVehicle = { ...existingVehicle, ...data }
    const validation = vehicleService.validateVehicleForTransaction(updatedVehicle)

    if (!validation.isValid) {
      return { success: false, errors: validation.errors }
    }

    const updated = await vehicleService.update(updatedVehicle)
    return { success: true, vehicle: updated }
  },
}
